import { useState, useEffect, useRef, useCallback } from "react";
import { cache } from "./useSWR.ts";
import { todayStr, formatDateFi } from "./utils.ts";
import { classifyClass } from "./classCategory.ts";

interface ClassEntry {
  id: string;
  date: string;
  productname: string;
  start: string;
  gym: string;
}

function collectClasses(): ClassEntry[] {
  const seen = new Set<string>();
  const entries: ClassEntry[] = [];

  for (const [key, entry] of cache.entries()) {
    if (!entry?.data) continue;

    if (key.startsWith("classes/")) {
      const date = key.replace("classes/", "");
      const events = entry.data as any[];
      if (!Array.isArray(events)) continue;
      for (const cls of events) {
        const uid = `${cls.id}:${date}`;
        if (seen.has(uid)) continue;
        seen.add(uid);
        entries.push({
          id: cls.id,
          date,
          productname: cls.productname || "",
          start: cls.start || "",
          gym: cls.employee_names?.[0]?.service_provider_name || "",
        });
      }
    } else if (key.startsWith("week/")) {
      const byDate = entry.data as Record<string, any[]>;
      if (!byDate || typeof byDate !== "object") continue;
      for (const [date, events] of Object.entries(byDate)) {
        if (!Array.isArray(events)) continue;
        for (const cls of events) {
          const uid = `${cls.id}:${date}`;
          if (seen.has(uid)) continue;
          seen.add(uid);
          entries.push({
            id: cls.id,
            date,
            productname: cls.productname || "",
            start: cls.start || "",
            gym: cls.employee_names?.[0]?.service_provider_name || "",
          });
        }
      }
    }
  }

  return entries;
}

function formatTime(dateStr: string): string {
  const match = dateStr.match(/(\d{2}):(\d{2})/);
  if (!match) return "";
  return `${match[1]}:${match[2]}`;
}

export function SearchPalette({
  onSelect,
  onClose,
}: {
  onSelect: (classId: string, date: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const allClasses = useRef<ClassEntry[]>([]);
  useEffect(() => {
    allClasses.current = collectClasses();
  }, []);

  const today = todayStr();
  const q = query.toLowerCase().trim();

  const filtered = allClasses.current.filter((c) => {
    if (!q) return true;
    return (
      c.productname.toLowerCase().includes(q) ||
      c.gym.toLowerCase().includes(q)
    );
  });

  // Sort: today first, then ascending date, then by start time
  filtered.sort((a, b) => {
    const aToday = a.date === today ? 0 : 1;
    const bToday = b.date === today ? 0 : 1;
    if (aToday !== bToday) return aToday - bToday;
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return a.start.localeCompare(b.start);
  });

  // Build items with dividers
  type Item =
    | { type: "class"; entry: ClassEntry; flatIndex: number }
    | { type: "divider"; label: string };

  const items: Item[] = [];
  let flatIndex = 0;
  let section: "today" | "other" | null = null;

  for (const entry of filtered) {
    const isToday = entry.date === today;
    if (isToday && section !== "today") {
      items.push({ type: "divider", label: "Tänään" });
      section = "today";
    } else if (!isToday && section !== "other") {
      items.push({ type: "divider", label: "Muut päivät" });
      section = "other";
    }
    items.push({ type: "class", entry, flatIndex });
    flatIndex++;
  }

  const selectableCount = flatIndex;

  // Clamp active index
  const clampedActive = Math.max(0, Math.min(activeIndex, selectableCount - 1));

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, selectableCount - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = filtered[clampedActive];
        if (item) onSelect(item.id, item.date);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    },
    [selectableCount, clampedActive, filtered, onSelect, onClose],
  );

  // Reset active index on query change
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(".search-result-active");
    el?.scrollIntoView({ block: "nearest" });
  }, [clampedActive]);

  // Auto-focus
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="search-backdrop" onClick={onClose}>
      <div className="search-palette" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="search-input"
          type="text"
          placeholder="Hae tuntia..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <div className="search-results" ref={listRef}>
          {items.length === 0 && (
            <div className="search-empty">Ei tuloksia</div>
          )}
          {items.map((item, i) => {
            if (item.type === "divider") {
              return (
                <div key={`d-${i}`} className="search-divider">
                  {item.label}
                </div>
              );
            }
            const { entry, flatIndex: fi } = item;
            const isActive = fi === clampedActive;
            return (
              <div
                key={`${entry.id}-${entry.date}`}
                className={`search-result${isActive ? " search-result-active" : ""}`}
                onClick={() => onSelect(entry.id, entry.date)}
                onMouseEnter={() => setActiveIndex(fi)}
              >
                <div className="search-result-name">
                  <span className="category-dot" style={{ backgroundColor: `var(--cat-${classifyClass(entry.productname)})` }} />
                  {entry.productname.trim()}
                </div>
                <div className="search-result-meta">
                  {formatTime(entry.start)} · {entry.gym}
                  {entry.date !== today && ` · ${formatDateFi(entry.date)}`}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
